<template>
  <div class="app-container operation-report-page">
    <div class="report-toolbar">
      <div>
        <div class="page-title">运营日报</div>
        <div class="page-subtitle">按 CRM 业务留痕自动汇总，重点看客户、商机、合同、售后和 C135 风险。</div>
      </div>
      <el-form :inline="true" :model="queryParams" class="toolbar-form">
        <el-form-item label="周期">
          <el-select v-model="queryParams.period" size="small" style="width: 110px" @change="handlePeriodChange">
            <el-option label="日报" value="day" />
            <el-option label="周报" value="week" />
            <el-option label="月报告" value="month" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="queryParams.period !== 'month'" label="日期">
          <el-date-picker
            v-model="queryParams.date"
            size="small"
            value-format="yyyy-MM-dd"
            type="date"
            placeholder="选择日期"
            :clearable="false"
            :picker-options="datePickerOptions"
            style="width: 150px"
          />
        </el-form-item>
        <el-form-item v-else label="月份">
          <el-date-picker
            v-model="queryParams.month"
            size="small"
            value-format="yyyy-MM"
            type="month"
            placeholder="选择月份"
            :clearable="false"
            :picker-options="datePickerOptions"
            style="width: 130px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" @click="loadReport">查询</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="resetToday">今天</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="summary-strip">
      <div class="metric-panel accent-blue">
        <div class="metric-label">有效业务动作</div>
        <div class="metric-value">{{ numberText(report.businessTraceCount) }}</div>
        <div class="metric-foot">客户、商机、合同、回款等</div>
      </div>
      <div class="metric-panel accent-green">
        <div class="metric-label">有业务记录人员</div>
        <div class="metric-value">{{ numberText(report.currentStaffBusinessTraceCount) }} / {{ numberText(report.currentStaffCount) }}</div>
        <div class="metric-foot">只统计营销与售后运营范围</div>
      </div>
      <div class="metric-panel accent-orange">
        <div class="metric-label">经营提醒</div>
        <div class="metric-value">{{ numberText(riskTotal) }}</div>
        <div class="metric-foot">C135、停滞、待完善记录</div>
      </div>
      <div class="metric-panel accent-red">
        <div class="metric-label">回款登记</div>
        <div class="metric-value small">{{ moneyText(report.fundAmount) }}</div>
        <div class="metric-foot">按有效业务人员登记口径</div>
      </div>
    </div>

    <div class="content-grid">
      <section class="report-section conclusion-section">
        <div class="section-title-row">
          <div>
            <div class="section-kicker">管理结论</div>
            <div class="section-title">老板先看这几件事</div>
          </div>
          <el-tag type="info" size="mini">更新时间：{{ report.cacheUpdateTime || report.queryTime || '-' }}</el-tag>
        </div>
        <div class="conclusion-list">
          <div v-for="(item, index) in conclusionList" :key="index" class="conclusion-item">
            <span class="index">{{ index + 1 }}</span>
            <span>{{ item }}</span>
          </div>
        </div>
      </section>

      <section class="report-section risk-section">
        <div class="section-title-row">
          <div>
            <div class="section-kicker">风险提醒</div>
            <div class="section-title">C135 与经营异常</div>
          </div>
          <el-button type="text" size="small" @click="activeTab = 'risk'">查看明细</el-button>
        </div>
        <div class="risk-list">
          <div v-for="item in riskList" :key="item.abnormalType" class="risk-item">
            <div class="risk-name">{{ item.abnormalType }}</div>
            <div class="risk-count">{{ numberText(item.abnormalCount) }}</div>
          </div>
          <div v-if="!riskList.length" class="empty-text">当前周期暂无经营提醒</div>
        </div>
      </section>
    </div>

    <div class="content-grid">
      <section class="report-section trend-section">
        <div class="section-title-row">
          <div>
            <div class="section-kicker">趋势</div>
            <div class="section-title">业务动作与过程记录走势</div>
          </div>
        </div>
        <div ref="trendChart" class="trend-chart"></div>
      </section>

      <section class="report-section trace-section">
        <div class="section-title-row">
          <div>
            <div class="section-kicker">记录分布</div>
            <div class="section-title">本期业务记录分布</div>
          </div>
        </div>
        <div class="trace-list">
          <div v-for="item in traceSummary" :key="item.category + item.traceName" class="trace-item">
            <div>
              <div class="trace-name">{{ item.traceName }}</div>
              <div class="trace-category">{{ item.category }}</div>
            </div>
            <div class="trace-count">{{ numberText(item.traceCount) }}</div>
          </div>
          <div v-if="!traceSummary.length" class="empty-text">暂无记录分布</div>
        </div>
      </section>
    </div>

    <section class="report-section detail-section">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="重点业务进展" name="progress">
          <el-table v-loading="loading" :data="report.keyProgressList || []" height="360">
            <el-table-column label="时间" prop="actionTime" width="170" align="center" />
            <el-table-column label="动作" prop="actionType" width="120" />
            <el-table-column label="人员" prop="userName" width="110" />
            <el-table-column label="客户" prop="relatedCustomer" min-width="160" show-overflow-tooltip />
            <el-table-column label="对象" prop="objectName" min-width="180" show-overflow-tooltip />
            <el-table-column label="摘要" prop="summary" min-width="260" show-overflow-tooltip />
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="人员业务留痕" name="users">
          <el-table v-loading="loading" :data="report.userTraceList || []" height="360" @row-click="openUserDrawer">
            <el-table-column label="人员" prop="nickName" min-width="120" fixed="left" />
            <el-table-column label="业务动作" prop="businessTraceCount" width="100" align="center" />
            <el-table-column label="过程记录" prop="processTraceCount" width="100" align="center" />
            <el-table-column label="协同/工单" prop="collaborationTraceCount" width="100" align="center" />
            <el-table-column label="系统活跃" prop="activeTraceCount" width="100" align="center" />
            <el-table-column label="使用端" prop="terminalUsage" min-width="150" />
            <el-table-column label="管理口径" min-width="160">
              <template slot-scope="scope">
                <el-tag v-if="Number(scope.row.businessTraceCount || 0) > 0" type="success" size="mini">有业务推进</el-tag>
                <el-tag v-else-if="Number(scope.row.totalTraceCount || 0) > 0" type="warning" size="mini">待补业务记录</el-tag>
                <el-tag v-else type="info" size="mini">暂无系统记录</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="经营提醒" name="risk">
          <el-table v-loading="loading" :data="report.abnormalSummaryList || []" height="360">
            <el-table-column label="提醒类型" prop="abnormalType" min-width="220" />
            <el-table-column label="数量" prop="abnormalCount" width="120" align="center" />
            <el-table-column label="建议动作" min-width="420">
              <template slot-scope="scope">{{ riskSuggestion(scope.row.abnormalType) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </section>

    <el-drawer
      :visible.sync="userDrawerOpen"
      :title="selectedUser ? selectedUser.nickName + ' 的' + periodLabel + '明细' : '人员明细'"
      size="520px"
      append-to-body
    >
      <div class="drawer-body">
        <div v-if="selectedUser" class="drawer-summary">
          <div>
            <span>业务动作</span>
            <strong>{{ numberText(selectedUser.businessTraceCount) }}</strong>
          </div>
          <div>
            <span>总记录</span>
            <strong>{{ numberText(selectedUser.totalTraceCount) }}</strong>
          </div>
          <div>
            <span>使用端</span>
            <strong>{{ selectedUser.terminalUsage || '-' }}</strong>
          </div>
        </div>
        <div v-for="item in selectedUserActions" :key="item.category + item.actionType + item.sourceTable" class="action-card">
          <div class="action-head">
            <span>{{ item.actionType }}</span>
            <el-tag size="mini" :type="item.category === '业务留痕' ? 'success' : 'info'">{{ item.category }}</el-tag>
          </div>
          <div class="action-count">{{ numberText(item.traceCount) }} 条</div>
          <div class="action-summary">{{ item.sampleSummary || '暂无摘要' }}</div>
        </div>
        <div v-if="!selectedUserActions.length" class="empty-text drawer-empty">暂无可读业务动作，建议确认是否需要补录客户或商机跟进。</div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getOperationReportSummary } from '@/api/crm/operationReport'

export default {
  name: 'OperationReport',
  data() {
    const today = this.formatDate(new Date())
    return {
      loading: false,
      activeTab: 'progress',
      queryParams: {
        period: 'day',
        date: today,
        month: today.slice(0, 7),
        detailLimit: 100
      },
      report: {},
      chart: null,
      userDrawerOpen: false,
      selectedUser: null
    }
  },
  computed: {
    riskList() {
      return (this.report.abnormalSummaryList || []).filter(item => Number(item.abnormalCount || 0) > 0)
    },
    riskTotal() {
      return this.riskList.reduce((sum, item) => sum + Number(item.abnormalCount || 0), 0)
    },
    traceSummary() {
      return (this.report.traceSummaryList || []).filter(item => Number(item.traceCount || 0) > 0).slice(0, 8)
    },
    conclusionList() {
      const report = this.report || {}
      return [
        `本周期有效业务动作 ${this.numberText(report.businessTraceCount)} 条，重点应看客户跟进、商机推进、合同和售后是否进入系统。`,
        `有效人员中 ${this.numberText(report.currentStaffBusinessTraceCount)} / ${this.numberText(report.currentStaffCount)} 人有业务记录，${this.numberText(report.currentStaffNoBusinessTraceCount)} 人需确认线下工作是否补录。`,
        `当前经营提醒 ${this.numberText(this.riskTotal)} 项，优先处理缺 C/关键人、缺 1W/EW/内线、商机停滞和待完善跟进记录。`
      ]
    },
    periodLabel() {
      const labelMap = {
        day: '日报',
        week: '周报',
        month: '月报告'
      }
      return labelMap[this.queryParams.period] || '周期'
    },
    selectedUserActions() {
      if (!this.selectedUser) {
        return []
      }
      const userId = String(this.selectedUser.userId)
      return (this.report.userActionDetailList || [])
        .filter(item => String(item.userId) === userId)
        .filter(item => item.category !== '系统日志' && item.category !== '系统活跃')
    },
    datePickerOptions() {
      return {
        disabledDate: this.isReportDateDisabled
      }
    }
  },
  mounted() {
    this.loadReport()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    loadReport() {
      this.normalizeQueryRange()
      const requestId = (this._reportRequestId || 0) + 1
      this._reportRequestId = requestId
      this.loading = true
      getOperationReportSummary(this.buildQuery())
        .then(response => {
          if (this._reportRequestId !== requestId) {
            return
          }
          this.report = response || {}
          this.$nextTick(() => this.renderTrendChart())
        })
        .catch(() => {
          if (this._reportRequestId === requestId) {
            this.report = {}
          }
        })
        .finally(() => {
          if (this._reportRequestId === requestId) {
            this.loading = false
          }
        })
    },
    buildQuery() {
      const params = {
        period: this.queryParams.period,
        detailLimit: this.queryParams.detailLimit
      }
      if (params.period === 'month') {
        params.month = this.queryParams.month
      } else {
        params.date = this.queryParams.date
      }
      return params
    },
    handlePeriodChange() {
      this.normalizeQueryRange()
      this.loadReport()
    },
    resetToday() {
      const today = this.formatDate(new Date())
      this.queryParams.period = 'day'
      this.queryParams.date = today
      this.queryParams.month = today.slice(0, 7)
      this.loadReport()
    },
    openUserDrawer(row) {
      this.selectedUser = row
      this.userDrawerOpen = true
    },
    renderTrendChart() {
      const el = this.$refs.trendChart
      if (!el) {
        return
      }
      if (!this.chart) {
        this.chart = echarts.init(el)
      }
      const trend = this.report.dailyTrendList || []
      this.chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0, data: ['业务动作', '过程记录', '协同/工单'] },
        grid: { left: 36, right: 18, top: 42, bottom: 28 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trend.map(item => item.traceDate)
        },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          {
            name: '业务动作',
            type: 'line',
            smooth: true,
            data: trend.map(item => item.businessTraceCount || 0),
            itemStyle: { color: '#0b7fab' },
            areaStyle: { color: 'rgba(11, 127, 171, 0.12)' }
          },
          {
            name: '过程记录',
            type: 'line',
            smooth: true,
            data: trend.map(item => item.processTraceCount || 0),
            itemStyle: { color: '#119c82' }
          },
          {
            name: '协同/工单',
            type: 'line',
            smooth: true,
            data: trend.map(item => item.collaborationTraceCount || 0),
            itemStyle: { color: '#d48a00' }
          }
        ]
      })
    },
    resizeChart() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    riskSuggestion(type) {
      if (!type) {
        return '-'
      }
      if (type.indexOf('缺 C') >= 0) {
        return '优先补齐商机关键人，明确谁能影响采购和技术决策。'
      }
      if (type.indexOf('1W') >= 0 || type.indexOf('EW') >= 0 || type.indexOf('内线') >= 0) {
        return '补充内线或关键联系人，避免商机只停留在普通联系人层面。'
      }
      if (type.indexOf('未推进') >= 0 || type.indexOf('下一步') >= 0) {
        return '要求负责人补下一步计划，并更新商机跟进内容。'
      }
      if (type.indexOf('待完善') >= 0) {
        return '补充客户对象、沟通结果和下一步动作，避免只写“已沟通”。'
      }
      return '进入对应业务对象核实责任人、状态和处理计划。'
    },
    moneyText(value) {
      const num = Number(value || 0)
      if (num >= 10000) {
        return `${(num / 10000).toFixed(2)} 万`
      }
      return `${num.toFixed(2)} 元`
    },
    numberText(value) {
      const num = Number(value || 0)
      return Number.isNaN(num) ? '0' : String(num)
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatMonth(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      return `${y}-${m}`
    },
    addDays(date, days) {
      const next = new Date(date.getTime())
      next.setDate(next.getDate() + days)
      return next
    },
    startOfWeek(date) {
      const day = date.getDay() || 7
      return this.addDays(date, 1 - day)
    },
    compareText(left, right) {
      return String(left || '').localeCompare(String(right || ''))
    },
    clampText(value, minValue, maxValue) {
      let next = value || maxValue
      if (this.compareText(next, minValue) < 0) {
        next = minValue
      }
      if (this.compareText(next, maxValue) > 0) {
        next = maxValue
      }
      return next
    },
    reportRange(period) {
      const today = new Date()
      if (period === 'month') {
        return {
          start: this.formatMonth(new Date(today.getFullYear(), today.getMonth() - 2, 1)),
          end: this.formatMonth(today)
        }
      }
      if (period === 'week') {
        return {
          start: this.formatDate(this.addDays(this.startOfWeek(today), -21)),
          end: this.formatDate(today)
        }
      }
      return {
        start: this.formatDate(this.addDays(today, -6)),
        end: this.formatDate(today)
      }
    },
    normalizeQueryRange() {
      if (this.queryParams.period === 'month') {
        const range = this.reportRange('month')
        this.queryParams.month = this.clampText(this.queryParams.month, range.start, range.end)
        return
      }
      const range = this.reportRange(this.queryParams.period)
      this.queryParams.date = this.clampText(this.queryParams.date, range.start, range.end)
    },
    isReportDateDisabled(time) {
      const period = this.queryParams.period
      const range = this.reportRange(period)
      const value = period === 'month' ? this.formatMonth(time) : this.formatDate(time)
      return this.compareText(value, range.start) < 0 || this.compareText(value, range.end) > 0
    }
  }
}
</script>

<style scoped>
.operation-report-page {
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.report-toolbar,
.report-section,
.metric-panel {
  background: #ffffff;
  border: 1px solid #e5edf0;
  border-radius: 8px;
}

.report-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 14px;
}

.page-title {
  color: #182b36;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
}

.page-subtitle {
  margin-top: 4px;
  color: #687985;
  font-size: 13px;
}

.toolbar-form {
  flex: none;
}

.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.metric-panel {
  position: relative;
  overflow: hidden;
  padding: 18px 18px 16px;
}

.metric-panel::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #0b7fab;
}

.metric-panel.accent-green::before {
  background: #119c82;
}

.metric-panel.accent-orange::before {
  background: #d48a00;
}

.metric-panel.accent-red::before {
  background: #c95757;
}

.metric-label {
  color: #6f7d86;
  font-size: 13px;
}

.metric-value {
  margin-top: 10px;
  color: #172631;
  font-size: 30px;
  font-weight: 700;
  line-height: 38px;
}

.metric-value.small {
  font-size: 25px;
}

.metric-foot {
  margin-top: 8px;
  color: #82909a;
  font-size: 12px;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.55fr);
  gap: 14px;
  margin-bottom: 14px;
}

.report-section {
  padding: 18px 20px;
}

.section-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-kicker {
  color: #0b7fab;
  font-size: 13px;
  font-weight: 700;
}

.section-title {
  margin-top: 4px;
  color: #172631;
  font-size: 18px;
  font-weight: 700;
}

.conclusion-list {
  display: grid;
  gap: 10px;
}

.conclusion-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #344550;
  font-size: 14px;
  line-height: 22px;
}

.conclusion-item .index {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e8f3f7;
  color: #0b7fab;
  font-size: 12px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
}

.risk-list,
.trace-list {
  display: grid;
  gap: 10px;
}

.risk-item,
.trace-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f7fafb;
  border: 1px solid #edf2f4;
  border-radius: 6px;
}

.risk-name,
.trace-name {
  color: #243642;
  font-size: 14px;
  font-weight: 600;
}

.risk-count,
.trace-count {
  color: #0b7fab;
  font-size: 22px;
  font-weight: 700;
}

.trace-category {
  margin-top: 4px;
  color: #84929d;
  font-size: 12px;
}

.trend-chart {
  width: 100%;
  height: 300px;
}

.detail-section {
  margin-bottom: 20px;
}

.empty-text {
  color: #8b98a3;
  font-size: 13px;
  line-height: 22px;
}

.drawer-body {
  padding: 0 20px 24px;
}

.drawer-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.drawer-summary > div {
  padding: 12px;
  background: #f7fafb;
  border-radius: 6px;
}

.drawer-summary span {
  display: block;
  color: #7a8790;
  font-size: 12px;
}

.drawer-summary strong {
  display: block;
  margin-top: 6px;
  color: #172631;
  font-size: 18px;
}

.action-card {
  padding: 14px 0;
  border-bottom: 1px solid #edf2f4;
}

.action-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #1d303c;
  font-weight: 700;
}

.action-count {
  margin-top: 8px;
  color: #0b7fab;
  font-size: 16px;
  font-weight: 700;
}

.action-summary {
  margin-top: 6px;
  color: #5f6d76;
  font-size: 13px;
  line-height: 21px;
}

.drawer-empty {
  padding-top: 20px;
}

@media (max-width: 1200px) {
  .summary-strip,
  .content-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .report-toolbar {
    display: block;
  }

  .toolbar-form {
    margin-top: 12px;
  }

  .summary-strip,
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
